import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useAppSelector } from '../../../store/hooks';
import { GridEventListener } from '@mui/x-data-grid';
import { provinceApi } from '../../../services';
import { ProvinceSchema } from '../../../services/provinceApi';
import { z } from 'zod';
import { MOBILE_BREAKPOINT, ROW_HEIGHTS, LIST_HEIGHTS } from '../../../utils/responsive';
import { useEntityListState, createEntityToolbar, type StatsQueryParams } from '../../../hooks';

type Province = z.infer<typeof ProvinceSchema>;
import { BaseDataGrid, PageHeader, ProvinceDialog, PersistentTabs, TabConfig } from '../../../components';
import { ProvincesMergedChart } from './ProvincesMergedChart';

const ProvincesToolbar = createEntityToolbar({
	searchPlaceholder: 'Tìm kiếm tỉnh thành...',
	exportFn: (params) => provinceApi.exportMergedToExcel(params),
});
import { ProvincesMergedChoropleth } from './ProvincesMergedChoropleth';
import { useProvincesColumns, useMergedProvincesQuery } from '../hooks';

interface ProvincesMergedContentProps {
	isAdmin: boolean;
	canEdit: boolean;
}

/**
 * Trang "Tỉnh/Thành phố (Sau sáp nhập)". Mỗi hàng ở đây là kết quả TỔNG HỢP
 * theo mã sau sáp nhập, không phải một hàng của bảng `tinh` gốc — hai không
 * gian mã đã phân kỳ sau đợt chuyển mã 2026 (một mã có thể trỏ tới hai tỉnh
 * khác nhau ở hai danh mục). Vì vậy trang này CHỈ XEM: không có Sửa/Xoá, và
 * click hàng chỉ mở dialog chỉ đọc. Sửa/xoá tỉnh gốc thực hiện ở trang
 * "Trước sáp nhập".
 */
export default function ProvincesMergedContent({ isAdmin, canEdit }: ProvincesMergedContentProps) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));
	const [searchParams, setSearchParams] = useSearchParams();
	const selectedFaculty = useAppSelector((state) => state.filters.selectedFaculty);

	const {
		searchValue, paginationModel, sortModel, filterModel,
		handlePaginationModelChange, handleSortModelChange,
		handleFilterModelChange, handleSearch,
	} = useEntityListState({ defaultSortField: 'tenTinh' });

	const [provinceDialog, setProvinceDialog] = useState<{
		open: boolean;
		mode: 'create' | 'view';
		province: Province | null;
	}>({ open: false, mode: 'create', province: null });
	const dialogRef = useRef(provinceDialog);
	dialogRef.current = provinceDialog;

	const handleCreate = useCallback(() => {
		setSearchParams({ action: 'create' });
	}, [setSearchParams]);

	const queryParams: StatsQueryParams = {
		filters: {
			query: searchValue,
			maKhoa: selectedFaculty ? [selectedFaculty] : undefined
		},
		page: paginationModel.page,
		pageSize: paginationModel.pageSize,
		sortBy: sortModel.length > 0 ? sortModel[0].field : undefined,
		sortOrder: (sortModel.length > 0 ? sortModel[0].sort : undefined) as 'asc' | 'desc' | undefined,
	};
	const { data: mergedProvincesData, isLoading: isMergedLoading, refetch: refetchMerged } = useMergedProvincesQuery(queryParams);
	const exportParams = useMergedProvincesQuery.buildExportParams(queryParams);

	const mergedProvinces = useMemo(() => mergedProvincesData?.data?.stats || [], [mergedProvincesData?.data?.stats]);
	const mergedTotalCount = mergedProvincesData?.pagination?.total || 0;

	// Sync URL params with dialog state. Trang này chỉ hỗ trợ 'create' (thêm
	// tỉnh gốc mới, không liên quan tới hàng nào trên lưới) và 'view' (xem
	// chi tiết một dòng tổng hợp). Mọi giá trị 'action' khác — kể cả 'edit' —
	// bị coi là không hợp lệ và quay về 'view' hoặc đóng dialog, vì lưới này
	// không có cách nào sửa đúng một hàng của bảng tỉnh gốc.
	useEffect(() => {
		const action = searchParams.get('action');
		const provinceId = searchParams.get('id');

		if (action === 'create' && !canEdit) {
			setSearchParams({});
			return;
		}

		if (action === 'create') {
			if (!dialogRef.current.open || dialogRef.current.mode !== 'create') {
				setProvinceDialog({
					open: true,
					mode: 'create',
					province: null,
				});
			}
			return;
		}

		if (action === 'view') {
			if (provinceId && mergedProvinces.length > 0) {
				if (dialogRef.current.open
					&& dialogRef.current.province?.maTinh === provinceId
					&& dialogRef.current.mode === 'view') {
					return;
				}
				const province = mergedProvinces.find((p) => p.maTinh === provinceId);
				if (province) {
					setProvinceDialog({
						open: true,
						mode: 'view',
						province: province as unknown as Province,
					});
				}
			}
			return;
		}

		if (action) {
			// Giá trị 'edit' hoặc bất kỳ giá trị lạ nào: không hợp lệ trên trang tổng hợp.
			if (provinceId) {
				setSearchParams({ action: 'view', id: provinceId });
			} else {
				setSearchParams({});
			}
			return;
		}

		if (dialogRef.current.open) {
			setProvinceDialog({ open: false, mode: 'create', province: null });
		}
	}, [searchParams, mergedProvinces, canEdit, setSearchParams]);

	const handleRefreshMerged = useCallback(() => {
		if (isMergedLoading) return;
		refetchMerged();
	}, [isMergedLoading, refetchMerged]);

	const handleRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
		setSearchParams({ action: 'view', id: (params.row as Province).maTinh });
	}, [setSearchParams]);

	const handleCloseProvinceDialog = useCallback(() => {
		setSearchParams({});
	}, [setSearchParams]);

	const mergedColumns = useProvincesColumns({
		isMobile,
		provinceStats: mergedProvincesData?.data,
	});

	const tabs: TabConfig[] = [
		{
			label: 'Danh sách',
			content: (
				<Box sx={{
					flex: 1,
					minHeight: 0,
					height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP,
					display: 'flex',
					flexDirection: 'column'
				}}>
					<BaseDataGrid
						rows={mergedProvinces}
						columns={mergedColumns}
						loading={isMergedLoading}
						onRowClick={handleRowClick}
						getRowId={(row) => row.maTinh}
						rowCount={mergedTotalCount}
						paginationModel={paginationModel}
						onPaginationModelChange={handlePaginationModelChange}
						sortModel={sortModel}
						onSortModelChange={handleSortModelChange}
						filterModel={filterModel}
						onFilterModelChange={handleFilterModelChange}
						pageSizeOptions={[10, 25, 50, 100]}
						paginationMode="server"
						sortingMode="server"
						filterMode="server"
						disableRowSelectionOnClick
						rowHeight={isMobile ? undefined : ROW_HEIGHTS.DESKTOP}
						getRowHeight={isMobile ? () => 'auto' : undefined}
						slots={{
							toolbar: () => (
								<ProvincesToolbar
									searchValue={searchValue}
									onSearch={handleSearch}
									onRefresh={handleRefreshMerged}
									exportParams={exportParams}
									isMobile={isMobile}
								/>
							),
						}}
					/>
				</Box>
			)
		},
		{
			label: 'Biểu đồ',
			content: (
				<Box sx={{ height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP, overflow: 'auto' }}>
					<ProvincesMergedChart height={500} />
				</Box>
			)
		},
		{
			label: 'Bản đồ',
			content: (
				<Box sx={{ height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP, display: 'flex', flexDirection: 'column' }}>
					<ProvincesMergedChoropleth fullHeight />
				</Box>
			)
		}
	];

	return (
		<Box sx={{
			width: '100%',
			height: '100vh',
			display: 'flex',
			flexDirection: 'column',
			overflow: 'hidden',
			p: 0
		}}>
			<PageHeader
				title="Tỉnh/Thành phố (Sau sáp nhập)"
				onRefresh={handleRefreshMerged}
				onAdd={canEdit ? handleCreate : undefined}
				isAdmin={isAdmin}
				loading={isMergedLoading}
			/>

			<PersistentTabs
				pageKey="provinces-merged"
				tabs={tabs}
				sx={{
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					minHeight: 0
				}}
			/>

			<ProvinceDialog
				open={provinceDialog.open}
				onClose={handleCloseProvinceDialog}
				province={provinceDialog.province}
				mode={provinceDialog.mode}
				merged={true}
			/>
		</Box>
	);
}

export { ProvincesMergedContent };
